import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play, Sparkles, TrendingUp, Users, Clock } from 'lucide-react';

const stats = [
  { icon: Users, value: '50+', label: 'Clients accompagnés' },
  { icon: TrendingUp, value: '95%', label: 'Satisfaction client' },
  { icon: Clock, value: '24/7', label: 'Systèmes autonomes' },
];

export const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-hero" />
      <div className="hero-glow" />
      <div className="hero-glow-bottom" />
      <div className="absolute inset-0 bg-grid-pattern opacity-30" />
      
      {/* Animated Orbs */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 0.15, scale: 1 }}
        transition={{ duration: 2, ease: 'easeOut' }}
        className="absolute top-1/4 -left-32 w-96 h-96 bg-primary rounded-full blur-[120px]"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 0.1, scale: 1 }}
        transition={{ duration: 2, delay: 0.5, ease: 'easeOut' }}
        className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent rounded-full blur-[120px]"
      />

      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-start gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6 max-w-2xl"
            >
              <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <span className="text-sm font-medium text-primary leading-snug">
                Stratégie Digital & IA - L’humain au cœur.
                <br />
                La technologie au service de votre croissance.
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6"
            >
              Des systèmes{' '}
              <span className="gradient-text">intelligents</span>
              {' '}pour développer votre activité{' '}
              <span className="text-muted-foreground">sans vous épuiser</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-lg text-muted-foreground mb-8 max-w-xl"
            >
              IA, automatisation, acquisition & organisation — tout commence par un diagnostic. 
              Nous accompagnons les TPE/PME dans la mise en place de systèmes clairs pour attirer, 
              suivre et convertir leurs prospects.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 mb-12"
            >
              <Link to="/contact">
                <Button variant="hero" size="xl" className="btn-glow group w-full sm:w-auto">
                  Demander un diagnostic
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/methode">
                <Button variant="outline" size="xl" className="group w-full sm:w-auto">
                  <Play className="w-5 h-5" />
                  Découvrir notre méthode
                </Button>
              </Link>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="grid grid-cols-3 gap-6"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1, duration: 0.6 }}
                  className="text-center sm:text-left"
                >
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                    <stat.icon className="w-4 h-4 text-primary" />
                    <span className="font-display text-2xl font-bold text-foreground">
                      {stat.value}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right - Hero Image/Dashboard */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
            className="relative lg:-mt-8"
          >
            <div className="relative mb-12">
              {/* Main Dashboard Card */}
              <div className="relative rounded-2xl overflow-hidden border border-border/50 shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5" />
                <img
                  src="https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=800&q=80"
                  alt="Dashboard Analytics Pulsia"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
              </div>

              {/* Floating Card - AI Assistant */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.6 }}
                className="absolute bottom-4 -left-6 p-4 rounded-xl glass-strong border border-primary/20 shadow-glow"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Assistant IA actif</p>
                    <p className="text-xs text-muted-foreground">24/7 • 12 requêtes traitées</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Card - Conversion */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="absolute -top-4 -right-4 p-4 rounded-xl glass-strong border border-accent/20"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">+47%</p>
                    <p className="text-xs text-muted-foreground">Conversion</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
